import { useState } from "react";
import { X, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth-context";
import * as api from "@/lib/api";
import type { SponsorAdPublic } from "@dugads/shared";

interface SponsorQuoteDialogProps {
  ad: SponsorAdPublic;
  schoolId?: string;
  onClose: () => void;
}

export function SponsorQuoteDialog({ ad, schoolId, onClose }: SponsorQuoteDialogProps) {
  const { user } = useAuth();
  const profile = user?.user;
  const [firstName, setFirstName] = useState(profile?.first_name ?? "");
  const [surname, setSurname] = useState(profile?.last_name ?? "");
  const [mobile, setMobile] = useState(profile?.phone_number ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.submitLead({
        config_id: ad.config_id,
        school_id: schoolId,
        first_name: firstName,
        surname,
        mobile,
      });
      setResult(res);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center overflow-y-auto p-4 sm:items-center">
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">{ad.provider_name}</CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
              <X className="h-4 w-4" />
            </Button>
          </div>
          {ad.tagline && <p className="text-sm text-muted-foreground">{ad.tagline}</p>}
        </CardHeader>
        <CardContent>
          {result ? (
            <div className="text-center py-4">
              <CheckCircle2 className={`h-12 w-12 mx-auto mb-3 ${result.success ? "text-green-600" : "text-amber-500"}`} />
              <p className="text-sm font-medium">{result.message}</p>
              <Button variant="outline" size="sm" className="mt-4" onClick={onClose}>Close</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <Label htmlFor="firstName">First Name</Label>
                <Input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="surname">Surname</Label>
                <Input id="surname" value={surname} onChange={(e) => setSurname(e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="mobile">Mobile Number</Label>
                <Input id="mobile" type="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="082 123 4567" required />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting...</> : ad.cta_label}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                By submitting, you agree to share your details with {ad.provider_name}.
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
